"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader as Loader2, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in duration-500">
        <div className="size-16 bg-primary/10 rounded-lg flex items-center justify-center mb-6">
          <CheckCircle2 className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground mb-2">Request Received</h3>
        <p className="text-muted-foreground mb-8 max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  const inputClassName = "border-x-0 border-t-0 border-b-2 border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary shadow-sm transition-colors rounded-md";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" className={inputClassName} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" className={inputClassName} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Phone Number (Optional)</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" className={inputClassName} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Company (Optional)</FormLabel>
              <FormControl>
                <Input placeholder="Organization Name" className={inputClassName} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="inquiryType" rules={{
      required: "Please select an inquiry type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Inquiry Type</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className={inputClassName}>
                  <SelectValue placeholder="Select the nature of your inquiry" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="admissions">Admissions & Enrollment</SelectItem>
                <SelectItem value="corporate">Corporate Training Programs</SelectItem>
                <SelectItem value="speaking">Speaking Engagements</SelectItem>
                <SelectItem value="general">General Information</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Please provide a message"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Your Message</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us more about your goals and how we can help..." className={`resize-none min-h-[120px] ${inputClassName}`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" size="lg" disabled={isSubmitting} className="w-full md:w-auto group">
        {isSubmitting ? <>
            <Loader2 className="mr-2 size-4 animate-spin" />
            Processing...
          </> : <>
            Submit Inquiry
            <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
          </>}
      </Button>
    </Form>;
}
export default function RequestMoreInformation() {
  return <section id="request-more-information" className="bg-background py-16 md:py-24 lg:py-32 overflow-hidden relative">
      <style dangerouslySetInnerHTML={{
      __html: `
          @keyframes ticker-scroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .animate-ticker-scroll {
            animation: ticker-scroll 30s linear infinite;
            display: flex;
            width: max-content;
          }
        `
    }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          
          {}
          <div className="lg:col-span-5 flex flex-col justify-between">
            <div>
              <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6 tracking-tight">
                Forge Your Voice
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-md">
                Take the first step towards mastering your narrative. Our admissions team is ready to guide you through our programs and help you find the perfect fit for your professional goals.
              </p>
            </div>

            {}
            <div className="mt-16 lg:mt-32 pt-8 border-t border-border overflow-hidden relative w-full">
              <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none"></div>
              <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none"></div>
              
              <div className="animate-ticker-scroll">
                {Array.from({
                length: 4
              }).map((_, i) => <div key={i} data-index={i} className="flex items-center whitespace-nowrap">
                    <span className="text-sm font-bold tracking-[0.2em] text-primary uppercase px-4">
                      STRATEGY
                    </span>
                    <span className="text-primary/30 px-2">•</span>
                    <span className="text-sm font-bold tracking-[0.2em] text-primary uppercase px-4">
                      CLARITY
                    </span>
                    <span className="text-primary/30 px-2">•</span>
                    <span className="text-sm font-bold tracking-[0.2em] text-primary uppercase px-4">
                      IMPACT
                    </span>
                    <span className="text-primary/30 px-2">•</span>
                  </div>)}
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7 bg-card/50 p-8 md:p-10 rounded-xl border border-border shadow-lg">
            <FormProvider formId="request-info-form-main" sectionName="Request More Information" defaultValues={{
            name: "",
            email: "",
            phone: "",
            company: "",
            inquiryType: "",
            message: ""
          }} submitText="Submit Inquiry" successMessage="Thank you for reaching out. Our admissions team will review your inquiry and contact you within 24-48 hours.">
              <ContactForm />
            </FormProvider>
          </div>

        </div>
      </div>
    </section>;
}